<template>
  <b-sidebar
    :id="id"
    ref="sidebar"
    :visible="isSidebarActive"
    sidebar-class="sidebar-lg"
    bg-variant="white"
    shadow
    backdrop
    no-header
    right
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >

    <template #default="{ hide }">
      <b-overlay
        :show="isLoading"
        rounded="sm"
        variant="secondary"
        opacity="0.75"
      >
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h5
            v-if="itemId || itemData.id"
            class="mb-0"
          > <span v-t="`modules.coupons.pages.edit`" />: {{ itemData.name || itemId }}</h5>
          <h5
            v-else
            v-t="`modules.coupons.pages.add`"
            class="mb-0"
          />

          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="hide"
          />

        </div>

        <validation-observer
          #default="{ handleSubmit }"
          ref="refFormObserver"
        >
          <!-- Body -->
          <b-form
            class="p-2"
            @submit.prevent="handleSubmit(onSubmit)"
            @reset.prevent="resetForm"
          >
            <div>
              <i-input
                id="name"
                v-model="itemData.name"
                :errors="validationErrors.name"
                type="text"
                title="inputs.name"
                description="modules.coupons.inputs.name:description"
              />
            </div>

            <div>
              <i-input
                id="code"
                v-model="itemData.code"
                :errors="validationErrors.code"
                type="text"
                title="modules.coupons.inputs.code"
                required
                :disabled="!!(itemId || itemData.id)"
              />
            </div>

            <hr>

            <!-- discount_type -->
            <b-form-group class="mb-2">
              <label>
                {{ $t('modules.coupons.inputs.discount_type') }}
              </label>
              <b-form-radio
                v-model="itemData.discount_type"
                class="mt-50 w-100"
                name="discount_type"
                value="fixed_discount"
              >
                {{ $t('modules.coupons.inputs.discount_type_options.fixed') }}
              </b-form-radio>
              <b-form-radio
                v-model="itemData.discount_type"
                class="mt-50 w-100"
                name="discount_type"
                value="percentage_discount"
              >
                {{ $t('modules.coupons.inputs.discount_type_options.percentage') }}
              </b-form-radio>
            </b-form-group>

            <!-- discount -->
            <validation-provider
              #default="{ errors }"
              rules="required|min:0"
              :name="$t('modules.coupons.inputs.discount_value')"
            >
              <b-form-group class="mb-2">
                <label>
                  {{ $t('modules.coupons.inputs.discount_value') }}
                </label>
                <b-input-group
                  :append="itemData.discount_type==='fixed_discount' ? getLocaleCurrency() : '%'"
                >
                  <b-form-input
                    v-model="itemData.discount_value"
                    placeholder="50"
                    type="number"
                    min="0"
                    :max="itemData.discount_type==='fixed_discount' ? 1000000 : 100"

                    :class="{'is-invalid' : isHasValidationError('discount_value', errors)}"
                    :aria-invalid="isHasValidationError('discount_value', errors)"
                  />
                </b-input-group>
              </b-form-group>
            </validation-provider>

            <!-- discount_segment -->
            <b-form-group class="mb-2">
              <label>
                {{ $t('modules.coupons.inputs.discount_segment') }}
              </label>
              <b-form-radio
                v-model="itemData.discount_segment"
                class="mt-50 w-100"
                name="discount_segment"
                value="products_price"
              >
                {{ $t('modules.coupons.inputs.discount_segment_options.products_price') }}
              </b-form-radio>
              <b-form-radio
                v-model="itemData.discount_segment"
                class="mt-50 w-100"
                name="discount_segment"
                value="shipping_fees"
              >
                {{ $t('modules.coupons.inputs.discount_segment_options.shipping_fees') }}
              </b-form-radio>
            </b-form-group>

            <!-- target_entities -->
            <template v-if="itemData.discount_segment === 'products_price'">
              <hr>

              <!-- target_type -->
              <b-form-group class="mb-2">
                <label>
                  {{ $t('modules.coupons.inputs.target_type') }}
                </label>
                <b-form-radio
                    v-model="itemData.target_type"
                    class="mt-50 w-100"
                    name="target_type"
                    value="all_products"
                    @input="onTargetTypeChange"
                >
                  {{ $t('modules.coupons.inputs.target_type_options.all_products') }}
                </b-form-radio>
                <b-form-radio
                    v-model="itemData.target_type"
                    class="mt-50 w-100"
                    name="target_type"
                    value="specific_products"
                    @input="onTargetTypeChange"
                >
                  {{ $t('modules.coupons.inputs.target_type_options.specific_products') }}
                </b-form-radio>
              </b-form-group>

              <!-- target_entities (product-groups) -->
              <b-form-group
                  v-if="itemData.target_type === 'specific_products'"
                  class="mb-2"
              >
                <label>
                  {{ $t('modules.coupons.inputs.target_entities') }}
                </label>
                <small class="text-muted d-block mb-1">
                  {{ $t('modules.coupons.inputs.target_entities:description') }}
                </small>
                <v-select
                    v-model="itemData.target_entities"
                    :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                    label="label"
                    :clearable="true"
                    :multiple="true"
                    :close-on-select="false"
                    :placeholder="$t('modules.coupons.inputs.target_entities:placeholder')"
                    :options="productGroupOptions"
                    :reduce="product => product.id"
                    :class="{'is-invalid' : validationErrors.target_entities && validationErrors.target_entities.length > 0}"
                />
                <small
                    v-if="validationErrors.target_entities && validationErrors.target_entities.length > 0"
                    class="text-danger"
                >
                  {{ validationErrors.target_entities[0] }}
                </small>
              </b-form-group>
            </template>

            <hr>
            <!-- discount_usability -->
            <b-form-group class="mb-2">
              <label>
                {{ $t('modules.coupons.inputs.discount_usability') }}
              </label>
              <b-form-radio
                v-model="itemData.discount_usability"
                class="mt-50 w-100"
                name="discount_usability"
                value="one_time"
              >
                {{ $t('modules.coupons.inputs.discount_usability_options.one_time') }}
              </b-form-radio>
              <b-form-radio
                v-model="itemData.discount_usability"
                class="mt-50 w-100"
                name="discount_usability"
                value="many_times"
              >
                {{ $t('modules.coupons.inputs.discount_usability_options.many_times') }}
              </b-form-radio>
              <b-form-radio
                v-model="itemData.discount_usability"
                class="mt-50 w-100"
                name="discount_usability"
                value="first_order"
              >
                {{ $t('modules.coupons.inputs.discount_usability_options.first_order') }}
              </b-form-radio>
            </b-form-group>
            <!-- is_commission_on_all_orders -->
            <!--            <b-form-group class="mb-2">-->
            <!--              <label>-->
            <!--                {{ $t('modules.coupons.inputs.is_commission_on_all_orders') }}-->
            <!--              </label>-->
            <!--              <b-form-radio-->
            <!--                v-model="itemData.is_commission_on_all_orders"-->
            <!--                class="mt-50 w-100"-->
            <!--                name="is_commission_on_all_orders"-->
            <!--                :value="false"-->
            <!--              >-->
            <!--                {{ $t('modules.coupons.inputs.is_commission_on_all_orders_options.0') }}-->
            <!--              </b-form-radio>-->
            <!--              <b-form-radio-->
            <!--                v-model="itemData.is_commission_on_all_orders"-->
            <!--                class="mt-50 w-100"-->
            <!--                name="is_commission_on_all_orders"-->
            <!--                :value="true"-->
            <!--              >-->
            <!--                {{ $t('modules.coupons.inputs.is_commission_on_all_orders_options.1') }}-->
            <!--              </b-form-radio>-->
            <!--            </b-form-group>-->

            <hr>

            <!-- start_at -->
            <b-form-group class="mb-2">
              <label>
                {{ $t('modules.coupons.inputs.start_at') }}
              </label>
              <small class="text-muted d-block mb-1">
                {{ $t('modules.coupons.inputs.start_at:description') }}
              </small>
              <flat-pickr
                v-model="itemData.start_at"
                class="form-control"
                :config="{ dateFormat: 'Y-m-d', altInput: true, altFormat: 'F j, Y' }"
              />
              <small
                v-if="validationErrors.start_at && validationErrors.start_at.length > 0"
                class="text-danger"
              >
                {{ validationErrors.start_at[0] }}
              </small>
            </b-form-group>

            <!-- end_at -->
            <b-form-group class="mb-2">
              <label>
                {{ $t('modules.coupons.inputs.end_at') }}
              </label>
              <small class="text-muted d-block mb-1">
                {{ $t('modules.coupons.inputs.end_at:description') }}
              </small>
              <flat-pickr
                v-model="itemData.end_at"
                class="form-control"
                :config="{ dateFormat: 'Y-m-d', altInput: true, altFormat: 'F j, Y' }"
              />
              <small
                v-if="validationErrors.end_at && validationErrors.end_at.length > 0"
                class="text-danger"
              >
                {{ validationErrors.end_at[0] }}
              </small>
            </b-form-group>

            <div>
              <i-input
                v-model="itemData.is_active"
                :errors="validationErrors.is_active"
                type="switch"
                title="inputs.is_active"
                description="inputs.is_active:description"
              >
                <span class="switch-icon-left">
                  <feather-icon icon="CheckIcon" />
                </span>
                <span class="switch-icon-right">
                  <feather-icon icon="XIcon" />
                </span>
              </i-input>
            </div>

            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button
                v-t="(itemId || itemData.id)?`Edit`:`Add`"
                variant="primary"
                class="mr-2"
                type="submit"
              />
              <b-button
                v-t="`Cancel`"
                variant="outline-secondary"
                @click="hide"
              />
            </div>
          </b-form>
        </validation-observer>

      </b-overlay>

    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BForm, BButton, BOverlay, BFormRadio, BFormGroup, BInputGroup, BFormInput,
} from 'bootstrap-vue'
import { onMounted, onUnmounted, ref } from 'vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import store from '@/store'
import couponStoreModule from '@/views/models/coupons/couponStoreModule'
import productStoreModule from '@/views/models/products/productStoreModule'
import useModelUpdate from '@/views/models/common/useModelUpdate'
import formValidation from '@core/comp-functions/forms/form-validation'
import { getLocaleCurrency } from '@core/utils/utils'

export default {
  components: {
    ValidationProvider,
    BFormInput,
    BInputGroup,
    BFormGroup,
    BFormRadio,
    ValidationObserver,
    BSidebar,
    BForm,
    BButton,
    BOverlay,
    vSelect,
    flatPickr,
  },
  directives: {
  },
  emits: ['refetch-data'],
  model: {
    prop: 'isSidebarActive',
    event: 'update:is-sidebar-active',
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    id: {
      type: String,
      default: 'sidebar-add-new-category',
      required: false,
    },
    itemId: {
      type: Number,
      default: null,
    },
    value: {
      type: Object,
      required: false,
      default: null,
    },
  },
  watch: {
    itemId(newValue) {
      if (newValue) {
        this.isLoading = true
        this.fetchItem(newValue).finally(() => {
          this.isLoading = false
        })
      } else {
        this.resetForm()
      }
    },
    isSidebarActive(newValue) {
      if (newValue) {
        // if (!this.itemId) {
        this.resetForm()
        // }
      } else {
        this.resetForm()
      }
    },
  },
  mounted() {
    // Set value of refs
    this.refs.value = this.$refs
  },
  methods: {
    getLocaleCurrency,
    onTargetTypeChange() {
      // Clear target_entities when switching to all_products
      if (this.itemData.target_type === 'all_products') {
        this.itemData.target_entities = []
      }
    },
  },
  setup(props, ctx) {
    const STORE_MODULE_NAME = 'coupons'
    const PRODUCTS_STORE_MODULE_NAME = 'products'

    // Register module
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, couponStoreModule)
    if (!store.hasModule(PRODUCTS_STORE_MODULE_NAME)) store.registerModule(PRODUCTS_STORE_MODULE_NAME, productStoreModule)
    onMounted(() => {
      if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, couponStoreModule)
      if (!store.hasModule(PRODUCTS_STORE_MODULE_NAME)) store.registerModule(PRODUCTS_STORE_MODULE_NAME, productStoreModule)
    })

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
      if (store.hasModule(PRODUCTS_STORE_MODULE_NAME)) store.unregisterModule(PRODUCTS_STORE_MODULE_NAME)
    })

    // Data definition
    const refs = ref({})
    const emptyItemData = {
      id: null,
      name: null,
      code: null,
      // Configs
      discount_type: 'fixed_discount',
      discount_value: null,
      discount_segment: 'products_price',
      discount_usability: 'many_times',
      // is_commission_on_all_orders: false,
      target_type: 'all_products',
      target_entities: [],
      start_at: null,
      end_at: null,

      is_active: true,
    }
    const itemData = ref({})
    const isLoading = ref(true)

    const countryOptions = ref([])
    const productGroupOptions = ref([])

    store.dispatch('coupons/fetchCountries')
      .then(response => { countryOptions.value = response.data.data })

    store.dispatch('products/fetchList', {'per_page': 100})
      .then(response => {
        productGroupOptions.value = response.data.data
      })

    const resetItemData = () => {
      itemData.value = JSON.parse(JSON.stringify(props.value || emptyItemData))
      isLoading.value = false
      ctx.emit('update:item-id', null)
    }

    resetItemData()

    const {
      refFormObserver,
      getValidationState,
      resetForm,
    } = formValidation(resetItemData)

    const { validationErrors, fetch, save } = useModelUpdate(
      STORE_MODULE_NAME,
      refs,
      itemData,
      () => {
        ctx.emit('refetch-data')
        ctx.emit('update:is-sidebar-active', false)
      },
      null,
      null,
      () => {
        resetItemData()

        ctx.emit('update:is-sidebar-active', false)
      },
    )

    const fetchItem = () => {
      if (itemData.value.id !== props.itemId) {
        itemData.value.id = props.itemId

        isLoading.value = true
        return fetch().finally(() => {
          isLoading.value = false
        })
      }
      return null
    }

    const onSubmit = () => {
      isLoading.value = true
      save(true,
        () => {
          isLoading.value = false
        }, () => {
          isLoading.value = false
        }, () => {
          isLoading.value = false
        })
    }

    const isHasValidationError = (attr, errors) => (errors.length > 0) || (validationErrors.value[attr] && validationErrors.value[attr].length > 0)

    return {
      itemData,
      fetchItem,
      validationErrors,
      onSubmit,

      refFormObserver,
      getValidationState,
      resetItemData,
      resetForm,
      isHasValidationError,

      refs,
      isLoading,
      countryOptions,
      productGroupOptions,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.vti__dropdown-list {
  z-index: 2;
}
</style>
